/* A walk-in that would still be running when that technician's next client
 * arrives.
 *
 * Two sheets in one, because there are two different problems and the desk can
 * only act on one of them:
 *
 *   the booking was REQUESTED   a promise to a person, so it is not offered to
 *                               be broken. The technician cannot be chosen, and
 *                               the list is of who else could take the WALK-IN
 *                               — that client is still standing at the desk.
 *   anything else               a warning. The list is of who else could take
 *                               the APPOINTMENT, because moving that is the
 *                               cheaper fix, and *Book anyway* is there for when
 *                               it is not.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const BookClash = {
  name: 'BookClash',
  setup: function () {
    var c = Vue.computed(function () { return store.ui.bookClash; });

    var staff = Vue.computed(function () {
      return c.value ? store.staffOf(c.value.staffId) : null;
    });

    var name = Vue.computed(function () {
      return (staff.value && staff.value.name) || 'That technician';
    });

    // Whole sentences rather than text stitched around {{ }}: the compiler
    // eats the whitespace either side of an interpolation and the seams show.
    var headline = Vue.computed(function () {
      if (!c.value) return '';
      return c.value.requested
        ? name.value + ' has been asked for at ' + store.formatClock(c.value.startsAt)
        : name.value + ' has a client at ' + store.formatClock(c.value.startsAt);
    });

    var runsTo = Vue.computed(function () {
      if (!c.value) return '';
      return c.value.serviceName + ' usually takes ' + c.value.minutes +
        ' minutes, so it would run to ' + store.formatClock(c.value.endsAt) + '.';
    });

    var booking = Vue.computed(function () {
      if (!c.value) return '';
      return c.value.appt.clientName + ' — ' + c.value.svc.name + ' at ' +
        store.formatClock(c.value.startsAt) + '.';
    });

    // Only worked out for the blocked case: it is a pass over the rotation,
    // and the warning does not use it.
    var others = Vue.computed(function () {
      if (!c.value || !c.value.requested) return [];
      return store.walkInAlternatives(c.value.staffId, c.value.serviceId);
    });

    var free = Vue.computed(function () {
      return (c.value && c.value.cover || []).filter(function (x) { return x.ok; });
    });

    // Why somebody cannot take the appointment, in the words the floor uses.
    function coverNote(x) {
      if (x.ok) return 'free';
      if (x.why === 'pinned') {
        return (x.requested ? 'requested at ' : 'booked at ') + store.formatClock(x.at);
      }
      if (x.why === 'busy') {
        return x.until ? 'in service to ' + store.formatClock(x.until) : 'in service';
      }
      if (x.why === 'skipped') return 'being skipped';
      if (x.why === 'shift') return 'gone by ' + store.formatClock(x.until);
      if (x.why === 'break') return (x.label || 'break') + ' at ' + store.formatClock(x.at);
      return '';
    }

    // The same for a colleague being offered the walk-in: free, or clashing
    // with something of their own. Swapping one clash for another silently
    // would be the same mistake twice.
    function altNote(o) {
      if (!o.clash) return 'free';
      return (o.clash.requested ? 'asked for at ' : 'booked at ') +
        store.formatClock(o.clash.startsAt);
    }

    var coverLine = Vue.computed(function () {
      if (!c.value) return '';
      var list = c.value.cover || [];
      if (!list.length) {
        return 'Nobody else in today does ' + c.value.svc.name + ', so it has to ' +
          'wait for ' + name.value + '.';
      }
      if (!free.value.length) return 'Nobody else is free when it runs:';
      return free.value.length +
        (free.value.length === 1 ? ' other tech could' : ' other techs could') +
        ' take that appointment instead:';
    });

    var altLine = Vue.computed(function () {
      if (!others.value.length) {
        return 'Nobody else free does ' + (c.value ? c.value.serviceName : 'it') + '.';
      }
      return 'Give the walk-in to somebody else:';
    });

    return {
      c: c, name: name, headline: headline, runsTo: runsTo, booking: booking,
      others: others, coverLine: coverLine, altLine: altLine,
      coverNote: coverNote, altNote: altNote,
      formatClock: store.formatClock,
      confirm: store.confirmBookClash,
      close: store.closeBookClash
    };
  },
  template: [
    '<div v-if="c" class="modal-scrim" @click.self="close">',
    '  <div class="sheet" role="alertdialog" aria-modal="true">',
    '    <div class="sheet__head sheet__head--warn">',
    '      <span class="sheet__letter sheet__letter--warn">{{ c.requested ? "\\u2605" : "!" }}</span>',
    '      <div>',
    '        <h3>{{ headline }}</h3>',
    '        <p>{{ runsTo }}</p>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <p class="warn-body">{{ booking }}</p>',
    '      <p v-if="c.requested" class="warn-body">',
    '        The client asked for {{ name }} by name, so this one is not offered around.',
    '      </p>',
    '    </div>',
    '',
    /* Requested: the appointment is not moving, so the list is of who could
       take the walk-in instead. */
    '    <div v-if="c.requested" class="assign__section">',
    '      <h4 class="assign__label">{{ altLine }}</h4>',
    '      <ul v-if="others.length" class="clash-list">',
    '        <li v-for="o in others" :key="o.staff.id" :class="{ \'is-free\': !o.clash }">',
    '          <span class="clash-list__who">{{ o.staff.name }}</span>',
    '          <span class="clash-list__why">{{ altNote(o) }}</span>',
    '        </li>',
    '      </ul>',
    '    </div>',
    '',
    /* Not requested: the cheaper fix is moving the appointment, so the list is
       of who could take that. */
    '    <div v-else class="assign__section">',
    '      <h4 class="assign__label">{{ coverLine }}</h4>',
    '      <ul v-if="c.cover.length" class="clash-list">',
    '        <li v-for="x in c.cover" :key="x.staff.id" :class="{ \'is-free\': x.ok }">',
    '          <span class="clash-list__who">{{ x.staff.name }}<note-warn :client-id="c.appt.clientId" :staff-id="x.staff.id" /></span>',
    '          <span class="clash-list__why">{{ coverNote(x) }}</span>',
    '        </li>',
    '      </ul>',
    '      <p class="sheet__hint">Nothing here moves the appointment on its own &mdash; that is a change to make in the booking system.</p>',
    '    </div>',
    '',
    '    <div class="sheet__actions">',
    '      <button v-if="!c.requested" type="button" class="btn btn--primary btn--lg" @click="confirm">',
    '        Book {{ name }} anyway</button>',
    '      <button type="button" :class="c.requested ? \'btn btn--primary btn--lg\' : \'btn btn--quiet\'"',
    '        @click="close">{{ c.requested ? "Got it" : "Cancel" }}</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
